import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  OutlineButton,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  LoadingSpinner,
  EmptyState
} from '../../styles/StyledComponents';
import { FundRequest } from '../../types';

const CashierFundRequests: React.FC = () => {
  const { user } = useAuth();
  const [fundRequests, setFundRequests] = useState<FundRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<FundRequest | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [assignedProjects, setAssignedProjects] = useState<any[]>([]);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: 'Cash',
    reference: '',
    expenseCategory: 'Fund Request Payment',
    supplier: 'Fund Request Payment',
    receiptNumber: '',
    receiptType: 'Tax Invoice' as 'Tax Invoice' | 'Invoice',
    notes: ''
  });

  useEffect(() => {
    loadAssignedProjects();
  }, []);

  const loadFundRequestsForAssignedProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading fund requests for assigned projects:', assignedProjects);
      
      // Get project IDs that the cashier is assigned to
      const projectIds = assignedProjects.map(project => project.id || project._id);
      
      console.log('Project IDs for filtering:', projectIds);
      
      if (projectIds.length === 0) {
        console.log('No assigned projects found, setting empty fund requests');
        setFundRequests([]);
        return;
      }

      // Get fund requests for each assigned project individually (workaround for backend filtering issue)
      let allFundRequests: FundRequest[] = [];
      
      for (const projectId of projectIds) {
        try {
          const params: any = {
            projectId: projectId
          };
          
          if (statusFilter !== 'all') {
            params.status = statusFilter;
          }
          
          console.log(`Loading fund requests for project ${projectId}:`, params);
          
          const response = await apiService.getFundRequests(params);
          if (response.success) {
            allFundRequests = [...allFundRequests, ...response.data];
          }
        } catch (err) {
          console.error(`Error loading fund requests for project ${projectId}:`, err);
        }
      }
      
      console.log('All fund requests loaded:', allFundRequests);
      setFundRequests(allFundRequests);
    } catch (err) {
      console.error('Error loading fund requests:', err);
      setError('Failed to load fund requests');
    } finally {
      setLoading(false);
    }
  }, [assignedProjects, statusFilter]);

  useEffect(() => {
    if (assignedProjects.length > 0) {
      loadFundRequestsForAssignedProjects();
    }
  }, [assignedProjects, statusFilter, loadFundRequestsForAssignedProjects]);

  const loadAssignedProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading assigned projects for user:', user?.id);
      
      // Get projects assigned to the current user (cashier)
      const response = await apiService.getAssignedProjects();
      if (response.success) {
        console.log('Assigned projects response:', response.data);
        setAssignedProjects(response.data);
      } else {
        setError(response.message || 'Failed to load assigned projects');
      }
    } catch (err) {
      console.error('Error loading assigned projects:', err);
      setError('Failed to load assigned projects');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayment = async () => {
    if (!selectedRequest) return;

    try {
      setProcessingPayment(true);
      const response = await apiService.processCashierPayment(selectedRequest.id!, paymentForm);
      
      if (response.success) {
        // Remove the processed request from the list
        setFundRequests(prev => prev.filter(req => req.id !== selectedRequest.id));
        setShowPaymentModal(false);
        setSelectedRequest(null);
        
        // Reset form
        setPaymentForm({
          paymentMethod: 'Cash',
          reference: '',
          expenseCategory: 'Fund Request Payment',
          supplier: 'Fund Request Payment',
          receiptNumber: '',
          receiptType: 'Tax Invoice',
          notes: ''
        });
        
        alert('Payment processed successfully! Expense has been recorded.');
      } else {
        alert(response.message || 'Failed to process payment');
      }
    } catch (err) {
      console.error('Error processing payment:', err);
      alert('Failed to process payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const openPaymentModal = (request: FundRequest) => {
    setSelectedRequest(request);
    setPaymentForm(prev => ({
      ...prev,
      reference: `FR-${request.id}`,
      receiptNumber: `FR-${request.id}`
    }));
    setShowPaymentModal(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Pending Cashier Payment':
        return <Badge variant="warning">{status}</Badge>;
      case 'Paid':
        return <Badge variant="success">{status}</Badge>;
      case 'Approved':
        return <Badge variant="info">{status}</Badge>;
      case 'Rejected':
        return <Badge variant="danger">{status}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <LoadingSpinner />
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: 'red', marginBottom: '1rem' }}>{error}</p>
            <Button onClick={loadAssignedProjects}>Retry</Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <h2>Fund Request Payments for Assigned Projects</h2>
          <p>Process payments for approved fund requests from your assigned projects</p>
        </CardHeader>
        <CardBody>
          {/* Status Filter */}
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <FormControl style={{ minWidth: '200px' }}>
              <FormLabel>Filter by Status</FormLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="Pending Cashier Payment">Pending Payment</option>
                <option value="Approved">Approved (Ready for Payment)</option>
                <option value="Paid">Paid</option>
                <option value="Under Review">Under Review</option>
                <option value="Rejected">Rejected</option>
                <option value="Draft">Draft</option>
              </Select>
            </FormControl>
            <Button onClick={loadFundRequestsForAssignedProjects} style={{ marginTop: '1.5rem' }}>
              Refresh
            </Button>
          </div>
          
          {/* Assigned Projects Info */}
          {assignedProjects.length > 0 && (
            <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <small>
                <strong>Assigned Projects:</strong> {assignedProjects.map(p => p.name).join(', ')}
              </small>
            </div>
          )}
          
          {/* Assignment Restriction Notice */}
          {assignedProjects.length > 0 && (
            <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
              <small>
                <strong>⚠️ Assignment Restriction:</strong> As a Cashier, you can only be assigned to one project at a time. 
                You will be available for new project assignments after your current project(s) are completed.
              </small>
            </div>
          )}
          


          {fundRequests.length === 0 ? (
            <EmptyState>
              <p>No fund request payments found for your assigned projects.</p>
              <p>Either you have no assigned projects or all fund requests have been processed.</p>
            </EmptyState>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Request ID</TableHeader>
                  <TableHeader>Project</TableHeader>
                  <TableHeader>Requester</TableHeader>
                  <TableHeader>Amount</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Submitted</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {fundRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <strong>#{request.id?.slice(-8)}</strong>
                    </TableCell>
                    <TableCell>
                      <div>
                        <strong>{request.projectName}</strong>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <strong>{request.requester}</strong>
                      </div>
                    </TableCell>
                    <TableCell>
                      <strong>{formatCurrency(request.amount, request.currency)}</strong>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(request.status)}
                    </TableCell>
                    <TableCell>
                      {new Date(request.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => openPaymentModal(request)}
                        disabled={request.status !== 'Approved' && request.status !== 'Pending Cashier Payment'}
                        style={{
                          backgroundColor: request.status === 'Approved' || request.status === 'Pending Cashier Payment' 
                            ? '#28a745' 
                            : '#6c757d'
                        }}
                      >
                        {request.status === 'Paid' ? 'Already Paid' : 
                         request.status === 'Approved' || request.status === 'Pending Cashier Payment' ? 'Process Payment' :
                         request.status === 'Rejected' ? 'Rejected' :
                         request.status === 'Draft' ? 'Draft' :
                         request.status === 'Submitted' ? 'Under Review' :
                         request.status === 'Under Review' ? 'Under Review' :
                         'View Details'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Payment Processing Modal */}
      {showPaymentModal && (
        <Modal isOpen={true}>
          <ModalOverlay onClick={() => setShowPaymentModal(false)} />
          <ModalContent>
            <ModalHeader>
              Process Payment for Fund Request
            </ModalHeader>
            <ModalBody>
            {selectedRequest && (
              <div>
                <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <h4>Request Details</h4>
                  <p><strong>Project:</strong> {selectedRequest.projectName}</p>
                  <p><strong>Amount:</strong> {formatCurrency(selectedRequest.amount, selectedRequest.currency)}</p>
                  <p><strong>Requester:</strong> {selectedRequest.requester}</p>
                </div>

                <FormControl>
                  <FormLabel>Payment Method</FormLabel>
                  <Select
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Check">Check</option>
                    <option value="Mobile Money">Mobile Money</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Reference Number</FormLabel>
                  <Input
                    type="text"
                    value={paymentForm.reference}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                    placeholder="Payment reference number"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Expense Category</FormLabel>
                  <Select
                    value={paymentForm.expenseCategory}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, expenseCategory: e.target.value }))}
                  >
                    <option value="Fund Request Payment">Fund Request Payment</option>
                    <option value="Materials">Materials</option>
                    <option value="Labor">Labor</option>
                    <option value="Equipment">Equipment</option>
                    <option value="Sub-Contracts">Sub-Contracts</option>
                    <option value="Others">Others</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Supplier/Service Provider</FormLabel>
                  <Input
                    type="text"
                    value={paymentForm.supplier}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, supplier: e.target.value }))}
                    placeholder="Supplier or service provider name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Receipt Number</FormLabel>
                  <Input
                    type="text"
                    value={paymentForm.receiptNumber}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, receiptNumber: e.target.value }))}
                    placeholder="Receipt or invoice number"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Receipt Type</FormLabel>
                  <Select
                    value={paymentForm.receiptType}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, receiptType: e.target.value as 'Tax Invoice' | 'Invoice' }))}
                  >
                    <option value="Tax Invoice">Tax Invoice</option>
                    <option value="Invoice">Invoice</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Notes</FormLabel>
                  <Textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes about the payment"
                    rows={3}
                  />
                </FormControl>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <OutlineButton
              onClick={() => setShowPaymentModal(false)}
              style={{ marginRight: '0.5rem' }}
            >
              Cancel
            </OutlineButton>
            <Button
              onClick={handleProcessPayment}
              disabled={processingPayment}
              style={{ backgroundColor: '#28a745' }}
            >
              {processingPayment ? 'Processing...' : 'Process Payment & Record Expense'}
            </Button>
          </ModalFooter>
        </ModalContent>
        </Modal>
      )}
    </div>
  );
};

export default CashierFundRequests;
